import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import CandidateAssessmentScoreContent from '../results/CandidateAssessmentScoreContent';

/**
 * Assessment score + proctoring modal for public hiring-manager review (share link).
 */
export default function PublicAssessmentScoreModal({ isOpen, onClose, shareCode, candidate }) {
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);

  const loadScore = useCallback(async () => {
    if (!shareCode || !candidate?._id) return;
    setLoading(true);
    setDetails(null);
    try {
      const res = await axios.get(
        `/api/screenings/public/candidate-review/${shareCode}/assessment/${candidate._id}/results`
      );
      setDetails(res.data);
    } catch (e) {
      if (e.response?.status === 404) {
        setDetails({ notAttempted: true });
      } else {
        toast.error(e.response?.data?.message || 'Failed to load assessment results');
        setDetails({ error: true, message: e.response?.data?.message || 'Failed to load' });
      }
    } finally {
      setLoading(false);
    }
  }, [shareCode, candidate?._id]);

  useEffect(() => {
    if (!isOpen) {
      setDetails(null);
      return;
    }
    loadScore();
  }, [isOpen, loadScore]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 overflow-y-auto h-full w-full z-50 bg-black/40 backdrop-blur-[2px]">
      <div className="relative top-10 mx-auto border w-11/12 sm:w-full sm:max-w-5xl shadow-xl rounded-xl bg-white max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h3 className="text-2xl font-bold text-gray-900">Candidate Results</h3>
            <div className="flex space-x-2">
              <span className="px-3 py-1.5 text-sm font-semibold rounded-md border bg-indigo-50 text-indigo-700 border-indigo-200">
                Assessment Score
              </span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">
          {loading && (
            <div className="flex justify-center py-16">
              <div className="h-10 w-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {!loading && <CandidateAssessmentScoreContent details={details} />}
        </div>
      </div>
    </div>
  );
}
